import { useEffect, useRef } from 'react';
import { HEX_SIZE, hexToPixel, pixelToHex } from '../lib/hex.js';
import type { MapViewport } from './HexMap.js';

export interface OverviewHive {
  id: number;
  q: number;
  r: number;
  ownerId: number | null;
  isMine: boolean;
  relation: 'ALLIED' | 'NEUTRAL' | 'HOSTILE' | null;
  queenless: boolean;
}
export interface OverviewField {
  q: number;
  r: number;
  resource: 'nectar' | 'pollen';
}

/**
 * Minimap der ganzen Welt (2D-Canvas): Felder als feine Punkte, Stoecke nach Beziehung gefaerbt,
 * Sichtbereich als Rahmen. Klick springt an die Stelle.
 */
export function MiniMap({
  hives,
  fields,
  radius,
  viewport,
  onJump,
  width = 180,
  height = 130,
}: {
  hives: OverviewHive[];
  fields: OverviewField[];
  radius: number;
  viewport: MapViewport | null;
  onJump: (q: number, r: number) => void;
  width?: number;
  height?: number;
}) {
  const ref = useRef<HTMLCanvasElement>(null);
  // Weltpixel-Ausdehnung fuer Radius: x max bei (q=radius, r=0) und (q=0, r=radius) usw.
  const ext = hexToPixel(radius, 0);
  const extR = hexToPixel(0, radius);
  const halfW = Math.max(ext.x, extR.x) + HEX_SIZE;
  const halfH = extR.y + HEX_SIZE;
  const scale = Math.min((width - 6) / (2 * halfW), (height - 6) / (2 * halfH));
  const toCanvas = (x: number, y: number) => ({
    x: width / 2 + x * scale,
    y: height / 2 + y * scale,
  });

  useEffect(() => {
    const c = ref.current;
    if (!c) return;
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    c.width = width * dpr;
    c.height = height * dpr;
    const ctx = c.getContext('2d');
    if (!ctx) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, width, height);
    ctx.fillStyle = 'rgba(12,10,9,0.85)';
    ctx.fillRect(0, 0, width, height);
    // Weltkreis
    ctx.strokeStyle = 'rgba(120,113,108,0.5)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.ellipse(width / 2, height / 2, halfW * scale, halfH * scale, 0, 0, Math.PI * 2);
    ctx.stroke();
    for (const f of fields) {
      const p = hexToPixel(f.q, f.r);
      const k = toCanvas(p.x, p.y);
      ctx.fillStyle = f.resource === 'nectar' ? 'rgba(245,208,32,0.45)' : 'rgba(244,114,182,0.45)';
      ctx.fillRect(k.x, k.y, 1, 1);
    }
    for (const h of hives) {
      const p = hexToPixel(h.q, h.r);
      const k = toCanvas(p.x, p.y);
      const r = h.isMine ? 3 : 1.8;
      ctx.fillStyle =
        h.ownerId === null
          ? 'rgba(168,162,158,0.55)'
          : h.isMine
            ? '#f59e0b'
            : h.relation === 'ALLIED'
              ? '#4ade80'
              : h.relation === 'HOSTILE'
                ? '#f87171'
                : '#d6d3d1';
      ctx.beginPath();
      ctx.arc(k.x, k.y, r, 0, Math.PI * 2);
      ctx.fill();
      if (h.queenless && h.ownerId !== null) {
        ctx.strokeStyle = '#c084fc';
        ctx.stroke();
      }
    }
    if (viewport) {
      const a = toCanvas(viewport.rect.x0, viewport.rect.y0);
      const b = toCanvas(viewport.rect.x1, viewport.rect.y1);
      ctx.strokeStyle = 'rgba(255,255,255,0.9)';
      ctx.lineWidth = 1;
      ctx.strokeRect(a.x, a.y, Math.max(3, b.x - a.x), Math.max(3, b.y - a.y));
    }
  }, [hives, fields, viewport, width, height, scale, halfW, halfH]);

  return (
    <canvas
      ref={ref}
      style={{ width, height }}
      className="cursor-crosshair rounded-lg border border-stone-700 shadow"
      title="Minimap – klicken zum Springen"
      onClick={(e) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const x = (e.clientX - rect.left - width / 2) / scale;
        const y = (e.clientY - rect.top - height / 2) / scale;
        const h = pixelToHex(x, y);
        onJump(h.q, h.r);
      }}
    />
  );
}
